import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AFFILIATE_ENABLED,
  AFFILIATE_INTERSTITIAL_ENABLED,
  AFFILIATE_INTERSTITIAL_MIN_NAVIGATIONS,
} from "../constant/constant";
import { AffiliateProduct } from "../data/affiliateProducts";
import { getProductsForPlacement } from "../data/affiliatePlacements";
import {
  canShowInterstitial,
  getRecentlyClickedIds,
  recordInterstitialShown,
} from "../service/affiliateStorageService";
import {
  getAffiliateCompletionEvent,
  getAffiliateNavigationCount,
  markAffiliateNavigation,
} from "../service/affiliateSessionService";

/**
 * Các route không bao giờ được chen interstitial: đang đăng nhập, đang làm bài,
 * hoặc đang đọc chính sách.
 */
const BLOCKED_PATHS = [
  "/login",
  "/register",
  "/practice-control",
  "/privacy",
  "/delete-account",
];

interface InterstitialState {
  product: AffiliateProduct | null;
  trigger: string;
}

/**
 * Quyết định khi nào được phép hiện interstitial.
 *
 * Điều kiện then chốt là KHÔNG BAO GIỜ bắn ở pageview đầu tiên của phiên.
 * Google hạ hạng mobile với interstitial xuất hiện ngay khi người dùng vừa vào
 * từ kết quả tìm kiếm, nên nó chỉ được bắn sau khi người dùng đã thật sự dùng
 * trang — hoàn thành một việc, hoặc đã chuyển trang vài lần.
 */
function useAffiliateInterstitial(): InterstitialState {
  const location = useLocation();
  const [state, setState] = useState<InterstitialState>({
    product: null,
    trigger: "",
  });

  useEffect(() => {
    markAffiliateNavigation();

    if (!AFFILIATE_ENABLED || !AFFILIATE_INTERSTITIAL_ENABLED) return;
    if (state.product) return;

    const navigations = getAffiliateNavigationCount();
    // navigations === 1 là pageview đầu tiên — luôn bỏ qua.
    if (navigations <= 1) return;

    if (BLOCKED_PATHS.some((path) => location.pathname.startsWith(path))) return;

    const completionEvent = getAffiliateCompletionEvent();
    const reachedNavThreshold =
      navigations >= AFFILIATE_INTERSTITIAL_MIN_NAVIGATIONS;
    if (!completionEvent && !reachedNavThreshold) return;

    if (!canShowInterstitial()) return;

    const [product] = getProductsForPlacement(
      "interstitial",
      1,
      getRecentlyClickedIds()
    );
    if (!product) return;

    recordInterstitialShown();
    setState({
      product,
      trigger: completionEvent ?? "navigation_threshold",
    });
  }, [location.pathname, state.product]);

  return state;
}

export default useAffiliateInterstitial;
